import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { apiRequest } from '@/services/appApi';
import { useAuth } from '@/contexts/AuthContext';

interface AppNotification {
  notification_id: string;
  event_type: string;
  title: string;
  body: string;
  read: boolean;
  created_at: number;
}

interface NotificationContextType {
  unreadCount: number;
  latestSignal: AppNotification | null;
  refreshUnread: () => void;
  clearLatestSignal: () => void;
}

const NotificationContext = createContext<NotificationContextType>({
  unreadCount: 0,
  latestSignal: null,
  refreshUnread: () => {},
  clearLatestSignal: () => {},
});

async function requestPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const perm = await Notification.requestPermission();
  return perm === 'granted';
}

function sendBrowserNotif(title: string, body: string, icon?: string) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    new Notification(title, { body, icon: icon || '/favicon.ico', tag: 'forexmind-notification' });
  } catch {
    // Browser notification failures should not block in-app state.
  }
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { accessToken } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestSignal, setLatestSignal] = useState<AppNotification | null>(null);

  const refreshUnread = useCallback(async () => {
    if (!accessToken) {
      setUnreadCount(0);
      return;
    }
    try {
      const data = await apiRequest<{ items: AppNotification[]; total: number }>(
        '/app/notifications?unread_only=true&page_size=20',
        {},
        accessToken,
      );
      setUnreadCount(Math.min(data.total || data.items.length, 9));
      const latest = data.items[0];
      if (latest && !latest.read) setLatestSignal(latest);
    } catch {
      setUnreadCount(0);
    }
  }, [accessToken]);

  const clearLatestSignal = useCallback(() => setLatestSignal(null), []);

  useEffect(() => {
    refreshUnread();
    requestPermission();
    if (!accessToken) return;
    const timer = window.setInterval(refreshUnread, 30_000);
    return () => window.clearInterval(timer);
  }, [accessToken, refreshUnread]);

  useEffect(() => {
    if (!latestSignal) return;
    sendBrowserNotif(latestSignal.title, latestSignal.body);
  }, [latestSignal]);

  return (
    <NotificationContext.Provider value={{ unreadCount, latestSignal, refreshUnread, clearLatestSignal }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  return useContext(NotificationContext);
}
